import { FC, ReactElement } from 'react'
import classNames from 'classnames'

import { ConnectedHelpTooltip } from '../../@paco/connectors'
import { Permission } from '../../@paco/entities/Permission/Permission'
import { transformLegacyRoleToRole } from '../../@paco/entities/Role/RoleTransformers'
import { AuthenticatedUser } from '../../@paco/entities/User/User'
import checkPermission from '../../@paco/helpers/permissions/checkPermission'
import trans from '../../@paco/helpers/trans'

import { getUserFullName } from '../../helpers'
import { convertDateToTime, formatDate } from '../../helpers/date'
import { getPermissionToEdit } from '../../helpers/permissions/getPermissionToAction'
import { translate } from '../../helpers/translations/translator'
import useTimeTicker from '../../helpers/useTimeTicker'
import { TrackViewModel } from '../../models'
import { getShiftStartAndEndTimeOfTrack } from '../../pages/Tracks/helpers/getShiftStartAndEndTimeOfTrack'
import TableContainer from '../Table/TableContainer'
import UserPageLink from '../UserPageLink/UserPageLink'

interface OpenTracksTableProps {
    currentUser: AuthenticatedUser;
    tracks: TrackViewModel[];
    permissions: Permission[];
    onStopClick: (track: TrackViewModel) => void;
    className?: string;
}

interface RunningTimeCellProps {
    from: string | Date;
}

const RunningTimeCell: FC<RunningTimeCellProps> = ({ from }) => {
    const time = useTimeTicker({ from })
    return <>{time}</>
}

const OpenTracksTable: FC<OpenTracksTableProps> = ({
    currentUser,
    tracks,
    permissions,
    onStopClick,
    className = '',
}) => {
    const renderStopButton = (track: TrackViewModel) => {
        const { roles: trackOwnerLegacyRoles = [] } = track.owner
        const trackOwnerRoles = trackOwnerLegacyRoles.map(transformLegacyRoleToRole)

        const isOwnTrack = currentUser.id === track.owner.id
        const isAllowedToApproveOwnTrack = isOwnTrack && checkPermission(permissions, 'approve-own-tracks')

        const canEditTrack = getPermissionToEdit(currentUser.role, trackOwnerRoles, permissions, 'tracks') || isAllowedToApproveOwnTrack
        const canStopTrack = ! isOwnTrack || checkPermission(permissions, 'edit-own-tracks-manager-app')

        const buttonClassNames = classNames('button-track-stop', {
            'button-track-stop--disabled': ! canEditTrack || ! canStopTrack,
        })

        const handleButtonClick = (): void => {
            if (! canEditTrack || ! canStopTrack) {
                return
            }

            onStopClick({ ...track, endedInManagerApp: true })
        }

        return (
            <button
                type="button"
                onClick={handleButtonClick}
                disabled={! canEditTrack || ! canStopTrack}
                className={buttonClassNames}
            >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                    <path d="M0 0h24v24H0z" fill="none" />
                    <path className="svg-button" fill="#FF1C25" d="M6 6h12v12H6z" />
                </svg>

                {translate('pages.tracks.stopTrack')}
            </button>
        )
    }

    const renderUserPageLink = (track: TrackViewModel): ReactElement | null => {
        if (! track.owner) {
            return null
        }

        return (
            <UserPageLink
                isPlain
                id={track.owner.id}
                name={getUserFullName(track.owner)}
                className="tracks-user-link"
            />
        )
    }

    return (
        <div className={className}>
            <TableContainer
                data={tracks}
                attributes={[
                    {
                        customAction: (track: TrackViewModel) => renderUserPageLink(track),
                        label: translate('common.name'),
                    },
                    {
                        customAction: (track: TrackViewModel) => formatDate(track.checkIn, 'd MMMM yyyy'),
                        label: translate('common.date'),
                    },
                    {
                        customAction: (track: TrackViewModel) => getShiftStartAndEndTimeOfTrack(track),
                        label: translate('common.shift'),
                    },
                    {
                        customAction: (track: TrackViewModel) => `${convertDateToTime(track.checkIn)}`,
                        label: translate('pages.tracks.startTime'),
                    },
                    {
                        customAction: (track: TrackViewModel) => <RunningTimeCell from={track.checkIn} />,
                        label: translate('pages.tracks.runningTime'),
                        className: 'td-timer',
                    },
                    {
                        customAction: (track: TrackViewModel) => renderStopButton(track),
                        label: '',
                        align: 'right',
                    },
                ]}
            />

            <ConnectedHelpTooltip
                index={2}
                route="open-tracks"
                subTitle={trans('help.tracks.trackData.title')}
                text={trans('help.tracks.trackData.text')}
                title={trans('help.tracks.title')}
                className="tracks__track-data-help-tooltip"
            />

            <ConnectedHelpTooltip
                index={3}
                showMobileInfoWarning
                route="open-tracks"
                subTitle={trans('help.tracks.stopTrack.title')}
                text={trans('help.tracks.stopTrack.text')}
                title={trans('help.tracks.title')}
                className="tracks__stop-track-help-tooltip"
            />
        </div>
    )
}

export default OpenTracksTable
